import { useState, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useGroupScopeReset,
  type GroupScopeResetTarget,
} from '@/hooks/api/useGroupScopeReset';
import { useToast } from '@/hooks/use-toast';

interface GroupScopeResetButtonProps {
  organizationId: string;
  /** The selected security group; `null` (organization scope) renders nothing. */
  groupId: string | null;
  groupName: string | null;
  /** Owner/manager only; everyone else gets no button. */
  canEdit: boolean;
  target: GroupScopeResetTarget;
}

/** The scope the confirmation opened in, and what it said it would remove. */
interface ConfirmScope {
  id: string;
  name: string;
  count: number;
}

function choices(count: number): string {
  return `${count} Allow/Block ${count === 1 ? 'choice' : 'choices'}`;
}

/**
 * KIR-4701: "Reset to organization default" beside the "Applies to" selector.
 * Drops every Allow/Block the selected security group set on the tab, after a
 * confirmation that says how many, so its members follow the organization's
 * choices again.
 */
export function GroupScopeResetButton({
  organizationId,
  groupId,
  groupName,
  canEdit,
  target,
}: GroupScopeResetButtonProps): ReactElement | null {
  const { count, isCountLoading, isCountError, reset, isResetting } =
    useGroupScopeReset(organizationId, groupId, target, canEdit);
  // What the confirmation said, kept after it closes so its content does not
  // blank out during the close animation; `open` alone opens and closes it.
  const [confirmScope, setConfirmScope] = useState<ConfirmScope | null>(null);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  // An open confirmation stays mounted when the scope leaves the group (it
  // was deleted, or the URL changed), so its guard below reports that rather
  // than the dialog vanishing and coming back for the next group. When the
  // organization's last group goes, the selector unmounts and the dialog with
  // it: nothing is written, and there is no scope left to report on.
  if (!canEdit || (!groupId && !open)) return null;

  const name = groupName ?? 'this group';
  const nothingSet = count === 0;
  const disabledReason = isCountError
    ? `Could not load the Allow/Block choices of ${name}. Reload the page to try again.`
    : nothingSet
      ? `${name} has no Allow/Block choices of its own on ${target.noun}.`
      : null;
  const button = groupId ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={
        isCountLoading ||
        isResetting ||
        count === undefined ||
        disabledReason !== null
      }
      onClick={() => {
        setConfirmScope({ id: groupId, name, count: count ?? 0 });
        setOpen(true);
      }}
    >
      Reset to organization default
    </Button>
  ) : null;

  return (
    <>
      {button && disabledReason ? (
        <Tooltip>
          {/* A disabled button fires no pointer events and skips tab order,
              so the trigger is a focusable wrapper. */}
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {button}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
      <ConfirmationDialog
        isOpen={open && confirmScope !== null}
        onClose={() => setOpen(false)}
        onConfirm={() => {
          if (!confirmScope) return;
          // The scope changed while the dialog was open: the reset would not
          // land on the group the dialog named.
          if (confirmScope.id !== groupId) {
            setOpen(false);
            toast({
              title: `${confirmScope.name} was not reset`,
              description:
                'The Allow/Block scope changed while the dialog was open. Check the scope and try again.',
              variant: 'destructive',
            });
            return;
          }
          void reset().then(() => setOpen(false));
        }}
        title={`Reset ${confirmScope?.name ?? name} to the organization default?`}
        description={
          confirmScope
            ? `This removes the ${choices(confirmScope.count)} set for ${confirmScope.name} on ${target.noun}. Members of ${confirmScope.name} then follow the organization's choices.`
            : ''
        }
        confirmLabel="Reset"
        confirmingLabel="Resetting..."
        isLoading={isResetting}
      />
    </>
  );
}
